import React, { useState, useEffect, useCallback } from 'react';
import { listUsers, adminCreateUser, adminDeleteUser, updateUserPassword } from '../api/services';
import { useAuth } from '../AuthContext';

const USER_LIMIT = 5;

function UserManagement({ setFeedback }) {
  const { user } = useAuth(); // Obtener el usuario logueado del contexto
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newUser, setNewUser] = useState({ email: '', password: '' });
  const [passwordChange, setPasswordChange] = useState({ currentPassword: '', newPassword: '' });

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const userList = await listUsers();
      setUsers(userList || []); // La lista ya viene filtrada desde el backend
    } catch (error) {
      console.error("Error fetching users:", error);
      setFeedback({ message: 'Error al cargar la lista de usuarios.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [setFeedback]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.password) {
      setFeedback({ message: 'El email y la contraseña son obligatorios.', type: 'error' });
      return;
    }
    setFeedback({ message: 'Creando usuario...', type: 'loading' });
    try {
      const response = await adminCreateUser(newUser.email, newUser.password);
      setFeedback({ message: response.message || 'Usuario creado con éxito.', type: 'success' });
      setNewUser({ email: '', password: '' });
      fetchUsers(); // Recargar la lista de usuarios
    } catch (error) {
      console.error("Error creating user:", error);
      // CORRECCIÓN: Asegurarse de que el mensaje de error sea siempre un string.
      setFeedback({ message: error.summary || error.message || 'No se pudo crear el usuario.', type: 'error' });
    }
  };

  const handleDeleteUser = async (username, groups) => {
    // Nueva validación: no permitir eliminar administradores
    if (groups && groups.includes('Admins')) {
      setFeedback({ message: 'No se puede eliminar a un usuario administrador.', type: 'error' });
      return;
    }
    if (!window.confirm(`¿Estás seguro de que quieres eliminar al usuario ${username}? Esta acción no se puede deshacer.`)) {
      return;
    }
    setFeedback({ message: `Eliminando a ${username}...`, type: 'loading' });
    try {
      await adminDeleteUser(username);
      setFeedback({ message: 'Usuario eliminado con éxito.', type: 'success' });
      fetchUsers(); // Recargar la lista
    } catch (error) {
      console.error("Error deleting user:", error);
      setFeedback({ message: 'No se pudo eliminar el usuario.', type: 'error' });
    }
  };

  const handlePasswordChangeInput = (e) => {
    const { name, value } = e.target;
    setPasswordChange(prev => ({ ...prev, [name]: value }));
  };

  const handleChangeMyPassword = async (e) => {
    e.preventDefault();
    if (!passwordChange.currentPassword || !passwordChange.newPassword) {
      setFeedback({ message: 'Ambos campos de contraseña son requeridos.', type: 'error' });
      return;
    }
    setFeedback({ message: 'Cambiando tu contraseña...', type: 'loading' });
    try {
      await updateUserPassword(passwordChange.currentPassword, passwordChange.newPassword);
      setFeedback({ message: 'Contraseña actualizada con éxito.', type: 'success' });
      setPasswordChange({ currentPassword: '', newPassword: '' });
    } catch (error) {
      console.error("Error changing password:", error);
      setFeedback({ message: error.summary || error.message || 'No se pudo cambiar la contraseña.', type: 'error' });
    }
  };

  // El límite ahora se calcula sobre el total de usuarios (incluyendo admins que no se ven)
  const canCreateUser = users.length < USER_LIMIT; // Esta lógica podría necesitar ajuste si el total de usuarios es mayor

  return (
    <section>
      <h2>Gestión de Usuarios</h2>
      
      <div className="user-creation-form">
        <h3>Crear Nuevo Usuario</h3>
        {canCreateUser ? (
          <form onSubmit={handleCreateUser} noValidate>
            <input
              type="email"
              name="email"
              placeholder="Email del nuevo usuario"
              value={newUser.email}
              onChange={handleInputChange}
              required
            />
            <input
              type="password"
              name="password"
              placeholder="Contraseña temporal"
              value={newUser.password}
              onChange={handleInputChange}
              required
            />
            <button type="submit">Crear Usuario</button>
          </form>
        ) : (
          <p className="feedback-message error">Límite de {USER_LIMIT} usuarios alcanzado. No se pueden crear más.</p>
        )}
      </div>

      <div className="user-list">
        <h3>Usuarios Existentes ({users.length}/{USER_LIMIT})</h3>
        {isLoading ? <div className="spinner" /> : (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Grupo</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.Username}>
                  <td>{user.email}</td>
                  <td>{user.groups.join(', ')}</td>
                  <td>
                    <button className="reject" onClick={() => handleDeleteUser(user.Username, user.groups)}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="user-creation-form password-change-form">
        <h3>Cambiar mi Contraseña</h3>
        <p>Aquí puedes cambiar la contraseña de tu propia cuenta de administrador ({user?.email}).</p>
        <form onSubmit={handleChangeMyPassword}>
          <input
            type="password"
            name="currentPassword"
            placeholder="Contraseña Actual"
            value={passwordChange.currentPassword}
            onChange={handlePasswordChangeInput}
            required
          />
          <input
            type="password"
            name="newPassword"
            placeholder="Nueva Contraseña"
            value={passwordChange.newPassword}
            onChange={handlePasswordChangeInput}
            required
          />
          <button type="submit">Cambiar Contraseña</button>
        </form>
      </div>
    </section>
  );
}

export default UserManagement;